/**
 * src/components/spatial/useStrictModeSafeLayer.ts
 *
 * Shared hook used by every SpatialObject*Layer component to register
 * its Mapbox/MapLibre layer in a way that survives React 18 dev-only
 * `<StrictMode>` synthetic remounts.
 *
 * The naive pattern (addLayer in mount, removeLayer in cleanup) breaks
 * silently in StrictMode dev because the cleanup runs between two
 * mounts that both want the same layer id. Even though Mapbox's
 * addLayer is idempotent in our provider abstraction, the cleanup of
 * pass 1 still fires its `removeLayer` AFTER pass 2's `addLayer` in
 * some Mapbox style states (the underlying gl-js style queue isn't
 * strictly synchronous), leaving the layer absent at steady state.
 *
 * The fix uses a module-scoped `Map<layerId, version>` to track which
 * mount currently owns each layer id. Cleanup defers its removal one
 * macrotask so a synchronous re-mount can claim the layer first; the
 * removal then no-ops because the version no longer matches.
 *
 * Production builds (no StrictMode double-mount) take the same code
 * path with a single mount/cleanup cycle, so behaviour is unchanged.
 */

import { useEffect, useRef } from 'react';
import type { SpatialMapProvider } from '../../services/spatial/mapProvider';

/** Generic layer spec — matches `provider.addLayer` first arg. */
type LayerSpec = Parameters<SpatialMapProvider['addLayer']>[0];

/**
 * Module-level registry of "who currently owns this layer id". Each
 * call to the hook bumps `nextVersion`; when a hook instance schedules
 * removal in its cleanup it only actually removes if the registry
 * still has its own version recorded.
 */
const layerVersionRegistry = new Map<string, number>();
let nextVersion = 0;

export interface UseStrictModeSafeLayerOptions {
  provider: SpatialMapProvider;
  /**
   * Build the layer spec lazily so we can capture the latest props
   * (filter / paint expressions) at the moment the style is ready.
   */
  buildSpec: () => LayerSpec;
  beforeId?: string;
  /** Layer id (must match the spec.id). Stable across re-renders. */
  layerId: string;
}

/**
 * Idempotent + StrictMode-safe layer add/remove. Returns nothing — the
 * effect manages the layer's lifecycle for the caller.
 */
export function useStrictModeSafeLayer({
  provider,
  buildSpec,
  beforeId,
  layerId,
}: UseStrictModeSafeLayerOptions): void {
  // Capture the latest buildSpec in a ref so the effect doesn't have
  // to re-run when the caller passes a fresh closure on every render.
  const buildSpecRef = useRef(buildSpec);
  buildSpecRef.current = buildSpec;

  useEffect(() => {
    nextVersion += 1;
    const myVersion = nextVersion;
    layerVersionRegistry.set(layerId, myVersion);

    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;

    provider.whenReady(() => {
      if (cancelled) return;
      // Mapbox-gl-js #11820: adding a layer SYNCHRONOUSLY inside the
      // map's `styledata` event handler (which is exactly where
      // `whenReady`'s deferred branch fires) leaves the new layer's
      // `layout` object un-instantiated until the next style re-eval.
      // The very next render frame then crashes inside
      // `draw_fill_extrusion.ts` at `layer.layout.get('fill-extrusion-
      // edge-radius')` with "Cannot read properties of undefined
      // (reading 'get')". The community-recommended workaround is to
      // schedule the addLayer call one macrotask later so Mapbox can
      // finish recalculating the style — by the next frame the layer's
      // layout/paint properties are fully wired up.
      //
      // Deferring also helps the synchronous branch (style already
      // loaded but our caller is itself running inside an event
      // dispatch like `style.load`), which is why we always queue.
      //
      // CRITICAL: `isStyleLoaded()` (and therefore `whenReady`) can
      // report ready while Mapbox's style queue hasn't actually
      // committed — the exact reason the GeoJSON source add uses a
      // polling retry. A single addLayer attempt then silently no-ops
      // (no throw, no error event) and the layer never appears, which
      // on a fresh page load leaves the spatial extrusions (the "blue
      // building") missing even though the source has data. So we
      // retry on a short bounded poll until the layer is verified
      // present.
      const MAX_ATTEMPTS = 50; // ~5s at 100ms — matches the source poll
      let attempts = 0;
      const tryAdd = () => {
        retryTimer = null;
        if (cancelled) return;
        if (layerVersionRegistry.get(layerId) !== myVersion) return;
        if (provider.hasLayer(layerId)) return; // success / idempotent
        try {
          provider.addLayer(buildSpecRef.current(), beforeId);
        } catch {
          /* style queue not committed yet — retry below */
        }
        if (provider.hasLayer(layerId)) return; // added successfully
        attempts += 1;
        if (attempts < MAX_ATTEMPTS) {
          retryTimer = setTimeout(tryAdd, 100);
        }
      };
      retryTimer = setTimeout(tryAdd, 0);
    });

    return () => {
      cancelled = true;
      if (retryTimer) { clearTimeout(retryTimer); retryTimer = null; }
      // Defer the removal a macrotask so a synchronous StrictMode
      // re-mount can update the registry version and effectively
      // cancel this removal. On a real unmount no newer mount comes,
      // so the registry still holds our version and we go through.
      setTimeout(() => {
        if (layerVersionRegistry.get(layerId) !== myVersion) return;
        layerVersionRegistry.delete(layerId);
        if (!provider.hasLayer(layerId)) return;
        provider.removeLayer(layerId);
      }, 0);
    };
    // beforeId and provider are the only stable identity inputs that
    // should retrigger the lifecycle; spec/paint/filter changes are
    // applied via dedicated `setPaintProperty` / `setFilter` effects
    // in the calling component.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provider, layerId, beforeId]);
}
